import { useLocale, useTranslations } from "../../contexts/Translation";
import { H2, H3 } from "../Headers/Headings";
import Section from "../Section";
import { useEffect, useState } from "react";
import { useApiData } from "../../contexts/ApiData";
import Button from "../Button/Button";
import Input, { Select } from "./Input";
import { OrgUser, ValidationError, ValidationResult } from "../../types";
import ErrorText from "./ErrorText";
import { useNavigate } from "react-router-dom";
import { useSurvey } from "../../contexts/Survey";
import { useOrganizationData, useOrganizations, useProfile, useRole } from "../../contexts/UserProvider";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import GroupAccess from "./GroupAccess";
import styles from "./FormStyles.module.css";
import { MAX_INVITATION_TEXT, MAX_SURVEY_NAME } from "../../lib/textLimits";

type SurveyFormData = {
  id: string;
  endDate: string;
  invitationText: string;
  surveyName: string;
  defaultLang: string;
  accessibleBy: string;
  onBehalfOf: string | null;
  groupsWithAccess?: string[];
  surveyType: "long-term" | "workshop" | "delivery";
  showRecommendation: boolean;
};

type SurveyFormProps = {
  isEdit?: boolean;
  onSuccess?: () => void;
};

const SurveyForm = ({ isEdit = false, onSuccess }: SurveyFormProps) => {
  const t = useTranslations("surveyForm");
  const role = useRole();
  const profile = useProfile();
  const locale = useLocale();
  const { post, put, get } = useApiData();
  const orgData = useOrganizationData();

  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [members, setMemebers] = useState<OrgUser[]>([]);
  const navigate = useNavigate();
  const { survey, reload } = useSurvey();
  const initialData: SurveyFormData = {
    id: survey?.id || crypto.randomUUID(),
    endDate: survey?.endDate.substring(0, 10) || new Date(Date.now() + 7 * 24 * 3600000).toISOString().substring(0, 10),
    invitationText: survey?.invitationText || "",
    surveyName: survey?.surveyName || "",
    defaultLang: survey?.defaultLang || locale || "",
    accessibleBy: survey?.accessibleBy || "me",
    onBehalfOf: survey?.onBehalfOf || profile?.userid || "",
    surveyType: survey?.surveyType || "long-term",
    showRecommendation: survey?.showRecommendation ?? true,
  };

  const [formData, setFormData] = useState<SurveyFormData>(initialData);
  const isOnbehalfOf = formData.onBehalfOf !== null && formData.onBehalfOf !== profile?.userid;
  const { currentOrganization, currentRole } = useOrganizations();

  useEffect(() => {
    const getUsers = async () => {
      const members = await get(`/organization/${currentOrganization}/users`);
      if (members) {
        setMemebers(members);
      }
    };
    if (currentOrganization && (role === "supporter" || role === "owner")) {
      getUsers();
    }
  }, [role, get, currentOrganization]);

  const validateData = ({ endDate }: SurveyFormData): ValidationResult => {
    const errors = [];
    const inOneWeek = new Date(Date.now() + 7 * 24 * 3600000).toISOString().substring(0, 10);

    if (!endDate) {
      errors.push({ name: "endDate", message: t("fieldRequired") });
    }
    if (!isEdit && endDate < inOneWeek) {
      errors.push({ name: "endDate", message: t("minimumDuration") });
    }

    return { errors: errors.length > 0 ? errors : undefined, success: errors.length === 0 };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>) => {
    setErrors(errors.filter((err) => err.name !== e.target.name));
    setFormData((prev) => {
      const value = e.target instanceof HTMLInputElement && e.target.type === "checkbox" ? e.target.checked : e.target.value;
      const name = e.target.name;
      const next = { ...prev, [name]: value } as SurveyFormData;
      if (name === "onBehalfOf") {
        // If onBehalfOf is someone else, force supporters; otherwise keep previous choice
        next.accessibleBy = value !== profile?.userid ? "supporters" : prev.accessibleBy;
      }

      if (name === "accessibleBy") {
        next.accessibleBy = value as string;
        if (value !== "selectedGroups") {
          next.groupsWithAccess = [];
        }
      }

      return next;
    });
  };

  const setGroupsWithAccess = (groups: string[]) => {
    setFormData({ ...formData, groupsWithAccess: groups });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResult = validateData(formData);

    if (validationResult.success) {
      const result = isEdit ? await put(`/survey/${formData.id}`, formData) : await post(`/survey/${formData.id}`, formData);
      if (!result) {
        setErrors([{ name: "form", message: t("serverError") }]);
      } else {
        if (isEdit) {
          await reload();
          onSuccess?.();
        } else {
          navigate(`/survey/${result.id}`);
        }
      }
    } else {
      setErrors(validationResult.errors || []);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  const getAccessibleByOptions = (): { value: string; text: string }[] => {
    if (["supporter", "owner"].includes(currentRole) && isOnbehalfOf) {
      return [
        { value: "supporters", text: t("accessibleBySupporters") },
        { value: "selectedGroups", text: t("accessibleBySelectedGroups") },
      ];
    }
    const options = [
      { value: "supporters", text: t("accessibleBySupporters") },
      { value: "selectedGroups", text: t("accessibleBySelectedGroups") },
      { value: "org", text: t("accessibleByOrganization") },
    ];
    if (orgData?.allowPrivateSurveys === 1) {
      options.unshift({ value: "me", text: t("accessibleByMe") });
    }
    return options;
  };

  return (
    <Section>
      <H2>{isEdit ? t("editHeading") : t("heading")}</H2>
      <form className={styles.form} onSubmit={handleSubmit}>
        {errors.filter((e) => e.name !== "form").length > 0 && <ErrorText variant='form' text={t("formErrors")} />}
        <ErrorText variant='form' error={errors.find((e) => e.name === "form")} />

        <div className={styles.fieldRow}>
          <Select
            options={[
              { value: "long-term", text: t("surveyTypeLongTerm") },
              { value: "workshop", text: t("surveyTypeWorkshop") },
              { value: "delivery", text: t("surveyTypeDelivery") },
            ]}
            handleChange={handleChange}
            readOnly={true}
            name='surveyType'
            label={t("surveyTypeLabel")}
            value={formData.surveyType}
            helpText={t("help.surveyType")}
            error={errors.find((e) => e.name === "surveyType")}
          />

          <Input
            error={errors.find((e) => e.name === "surveyName")}
            name='surveyName'
            maxLength={MAX_SURVEY_NAME}
            handleChange={handleChange}
            label={t("surveyName")}
            value={formData.surveyName}
          />
        </div>
        {members && members.length > 0 && (role === "supporter" || role === "owner") && (
          <div className={styles.fieldRow}>
            <Select
              options={members.map((m) => ({ value: `${m.userId}`, text: `${m.candidate}` }))}
              handleChange={handleChange}
              name='onBehalfOf'
              helpText={t("help.onBehalfOf")}
              label={t("onBehalfOf")}
              value={formData.onBehalfOf || ""}
            />
          </div>
        )}
        <div className={styles.fieldRow}>
          <Input
            error={errors.find((e) => e.name === "endDate")}
            type='date'
            name='endDate'
            helpText={t("help.endDate")}
            handleChange={handleChange}
            label={t("endDate")}
            value={formData.endDate}
          />

          <LanguageSelector
            handleChange={handleChange}
            name='defaultLang'
            helpText={t("help.defaultLang")}
            label={t("defaultLang")}
            value={formData.defaultLang}
          />
        </div>

        <div className={styles.fieldRow}>
          <Input
            type='textarea'
            helpText={t("help.invitationText")}
            name='invitationText'
            maxLength={MAX_INVITATION_TEXT}
            handleChange={handleChange}
            label={t("invitationText")}
            value={formData.invitationText}
          />
        </div>
        <Input
          type='checkbox'
          name='showRecommendation'
          handleChange={handleChange}
          label={t("showRecommendation")}
          value={formData.showRecommendation}
          infoText={t("showRecommendationInfo")}
        />

        <div className={styles.section}>
          <H3>{t("accessHeading")}</H3>
          <div className={styles.fieldRow}>
            <Select
              options={getAccessibleByOptions()}
              readOnly={!currentOrganization}
              handleChange={handleChange}
              name='accessibleBy'
              helpText={t("help.accessibleBy")}
              label={t("accessibleBy")}
              value={formData.accessibleBy}
            />
          </div>
          {formData.accessibleBy === "selectedGroups" && (
            <GroupAccess initiallySelected={survey?.groupsWithAccess || []} setGroupsWithAccess={setGroupsWithAccess} />
          )}
        </div>

        <Button>{t(isEdit ? "submitEdit" : "submit")}</Button>
      </form>
    </Section>
  );
};

export default SurveyForm;
